import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useAppDispatch, useAppSelector } from "hooks";
import { WsDialogs } from "interfaces";
import {
	onCloseDIP,
	onDIPtrigger,
	selectDipState,
	setPricePoint,
	setTriggerOnCache
} from "redux/slices";
import { offersApi } from "redux/apis";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { useRewardItems } from "./hooks/useRewardItems";
import { CustomDialog } from "./styled";
import { BundleItem } from "./variants/BundleItem";
import { SingleItem } from "./variants/SingleItem";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { IPlaygamiAnalyticProps } from "interfaces/analytics/playgami";
import { useEffect } from "react";
import classNames from "classnames";
import { FeaturesFlags } from "helpers/flagsConfig";
import { useEnvContext } from "contexts";

export type LoyaltyRewardItem =
	// [id: string]: number;
	Map<string, number> | never[];

export interface ILoyaltyRewardBundleItem {
	id: string;
	amount: number;
	imageUrl: string;
	pluralLabel: string;
	singularLabel: string;
}

const DialogLoyaltyPurchaseConfirm = ({
	show,
	onCloseClick
}: {
	show: boolean;
	// eslint-disable-next-line
	onCloseClick: (event?: any, reason?: string) => void;
}) => {
	const dispatch = useAppDispatch();
	const {
		wsLoyaltyBuy,
		authToken,
		userTracker,
		handleRefresh,
		setCurrentOffer
	} = useWebStoreContext();

	const { reportLoyaltyPurchaseEvent } = useGoogleAnalyticsContext();
	const { openDialog, dialogState } = useDialogModalRedux();
	const { items, data } = dialogState;
	const { hasRewardItems, rewardItems } = useRewardItems(items);
	const { data: dipsData } = useAppSelector(selectDipState);
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { isFeatureFlagEnabled } = useEnvContext();

	const cbClose = () => {
		// setIsCurrentOffer(false);
		setCurrentOffer(null);
	};

	const isDealyDealsSetion = data?.sectionName === "daily_deals";
	const hideRewardsDetails =
		isDealyDealsSetion &&
		isFeatureFlagEnabled(FeaturesFlags.LOYALTY_SHOP_CAROUSEL);

	const scale = useCalculateScale(
		400 + 40,
		!hideRewardsDetails ? 440 + 40 : 340 + 40
	);

	const reportAnalytics = ({
		event_name,
		offer_id,
		reward_id,
		reward_type,
		value_int,
		value_int2,
		value_int3
	}: IPlaygamiAnalyticProps) => {
		const event_type = "loyalty_purchase_confirm_dialog";
		reportPlaygamiEvent("tp.web_frontend", {
			event_name,
			event_type,
			action: event_name,
			source: event_type,
			offer_id,
			reward_id,
			reward_type,
			value_int,
			value_int2,
			value_int3
		});
	};

	const onClickBuy = async (offerId: string) => {
		reportAnalytics({
			event_name: "loyalty_buy_click",
			offer_id: offerId,
			reward_id: data?.rewardId,
			reward_type: `${data?.rewardId}_${data?.description}`,
			value_int: data?.loyaltyPointPrice,
			value_int2: Number(data?.crossOutValue),
			value_int3:
				(userTracker?.loyaltyPoints || 0) - (data?.loyaltyPointPrice || 0)
		});

		if (authToken?.value) {
			const success = await wsLoyaltyBuy(offerId ?? "", data?.dipId);
			if (success) {
				openDialog(WsDialogs.LOYALTY_PURCHASE_SUCCESS, { cbClose });

				if (data) {
					reportLoyaltyPurchaseEvent(
						"loyalty_purchase_confirmed",
						userTracker?.id as string,
						data.endTime,
						offerId,
						data.rewardId,
						data.priority,
						data.loyaltyPointPrice,
						data.campaignGroup,
						data.sectionName
					);
				}
				handleRefresh();
				dispatch(onCloseDIP());
				dispatch(offersApi.util.invalidateTags(["offersTag"]));
				if (
					dipsData?.dips?.find(
						(obj) => obj.dip.triggerType === "purchaseSuccess-loyalty_points"
					)
				) {
					dialogState.itemPrice &&
						dispatch(setPricePoint(dialogState.itemPrice));
					dispatch(setTriggerOnCache("purchaseSuccess-loyalty_points"));
				} else {
					dispatch(setTriggerOnCache("loyaltyPurchaseSuccess"));
				}
				return;
			}
			cbClose();
			dispatch(onDIPtrigger({ triggerType: "purchaseFailed" }));

			//NOTE:bfloyd error cases already handled for undefined/null invoice
		}
	};

	const onClose = () => {
		reportAnalytics({
			event_name: "dialog_x_close"
		});
		onCloseClick?.();
	};

	const itemProps = {
		onClose,
		onClickBuy,
		rewardItems,
		hideRewardsDetails
	};

	useEffect(() => {
		if (show) {
			reportAnalytics({
				event_name: "dialog_shown"
			});
		}
	}, [show]);

	if (!dialogState?.offerId) {
		return null;
	}

	return (
		<CustomDialog
			className={classNames({
				"bundle-item": hasRewardItems,
				"single-item": !hasRewardItems,
				"hide-reward-details": hideRewardsDetails
			})}
			open={show}
			onClose={onClose}
			scale={scale}
		>
			{hasRewardItems ? (
				<BundleItem {...itemProps} />
			) : (
				<SingleItem {...itemProps} />
			)}
		</CustomDialog>
	);
};

export { DialogLoyaltyPurchaseConfirm };
